const MAX_BALE_NUMBER = 99999
const MIN_BALE_NUMBER = 1

/**
 * Parses a printer-like bale number string (e.g. "1-5, 4-7") into an array of numbers.
 * Allows duplicates, ignores spaces, and supports unordered/overlapping ranges.
 *
 * @param {string} input - The bale number string to parse.
 * @returns {number[]} An array of bale numbers.
 */
export function parseBalesStringToArray(input: string): number[] {
  if (!input.trim()) return []
  return input
    .replace(/\s+/g, '') // Remove all spaces
    .split(',')
    .flatMap((part) => {
      // If range separated by a hyphen, split and create range
      if (part.includes('-')) {
        const [startStr, endStr] = part.split('-')
        const start = Number(startStr)
        const end = Number(endStr)
        // If either start or end is missing, just return the defined number (if valid)
        if (startStr === '' && !isNaN(end) && end >= MIN_BALE_NUMBER) return [end]
        if (endStr === '' && !isNaN(start) && start >= MIN_BALE_NUMBER) return [start]
        if (isNaN(start) || isNaN(end)) return []

        // Ensure both start and end are less than six digits
        if (start < 1 || end < 1 || start > MAX_BALE_NUMBER || end > MAX_BALE_NUMBER) return []

        // Allow descending ranges as well
        const step = start <= end ? 1 : -1
        return Array.from({ length: Math.abs(end - start) + 1 }, (_, i) => start + i * step).filter(
          (num) => num >= 1
        ) // Only allow numbers >= 1
      }
      // Else, return single number
      const num = Number(part)
      // If number is invalid or out of range, return empty array
      return isNaN(num) || num < MIN_BALE_NUMBER || num > MAX_BALE_NUMBER ? [] : [num]
    })
    .sort((a, b) => a - b) // Sort the final array
}

/**
 * Converts an array of numbers into a printer-like range string.
 * Duplicates are preserved, but invalid or out-of-range numbers are ignored.
 *
 * @param {number[]} numbers - The array of bale numbers.
 * @returns {string} The printer-like range string.
 */
export function parseBalesArrayToString(numbers: number[]): string {
  if (!Array.isArray(numbers) || numbers.length === 0) return ''

  // Filter out invalid numbers (non-integers, <1, >MAX_BALE_NUMBER, NaN)
  const filtered = numbers
    .filter(
      (n) =>
        typeof n === 'number' &&
        Number.isInteger(n) &&
        n >= MIN_BALE_NUMBER &&
        n <= MAX_BALE_NUMBER &&
        !isNaN(n)
    )
    .slice() // shallow copy to avoid mutating input

  if (filtered.length === 0) return ''

  filtered.sort((a, b) => a - b)

  // Use reduce to group consecutive numbers into ranges
  const ranges = filtered.reduce<{ rangeStart: number; rangeEnd: number }[]>(
    (groups, currentNumber) => {
      if (groups.length === 0) {
        groups.push({ rangeStart: currentNumber, rangeEnd: currentNumber })
      } else {
        const lastGroup = groups[groups.length - 1]
        if (currentNumber === lastGroup.rangeEnd + 1) {
          lastGroup.rangeEnd = currentNumber
        } else {
          groups.push({ rangeStart: currentNumber, rangeEnd: currentNumber })
        }
      }
      return groups
    },
    []
  )

  return ranges
    .map(({ rangeStart, rangeEnd }) =>
      rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`
    )
    .join(', ')
}
